import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SEO from '../components/common/SEO';
import Breadcrumb from '../components/common/Breadcrumb';
import SafeIcon from '../components/common/SafeIcon';
import TypeBadge from '../components/pokemon/TypeBadge';
import { getTopPokemonByStats } from '../data/pokemonData';

const { FiTrendingUp, FiZap, FiHeart, FiShield, FiTarget, FiStar } = FiIcons;

// Define ranking configurations outside the component to prevent unnecessary re-renders
const rankingTypes = {
  'fastest': { 
    stat: 'speed', 
    title: 'Fastest Pokemon', 
    description: 'Pokemon ranked by their base Speed stat',
    icon: FiZap,
    color: 'yellow'
  },
  'strongest': { 
    stat: 'attack', 
    title: 'Strongest Pokemon', 
    description: 'Pokemon ranked by their base Attack stat',
    icon: FiTrendingUp,
    color: 'red'
  },
  'bulkiest': { 
    stat: 'hp', 
    title: 'Bulkiest Pokemon', 
    description: 'Pokemon ranked by their base HP stat',
    icon: FiHeart,
    color: 'green'
  },
  'defensive': { 
    stat: 'defense', 
    title: 'Most Defensive Pokemon', 
    description: 'Pokemon ranked by their base Defense stat',
    icon: FiShield,
    color: 'blue'
  },
  'special-attack': { 
    stat: 'specialAttack', 
    title: 'Highest Special Attack', 
    description: 'Pokemon ranked by their base Special Attack stat',
    icon: FiTarget,
    color: 'purple'
  },
  'special-defense': { 
    stat: 'specialDefense', 
    title: 'Highest Special Defense', 
    description: 'Pokemon ranked by their base Special Defense stat',
    icon: FiShield,
    color: 'indigo'
  }
};

function TopRankingsPage() {
  const { type } = useParams();
  const [pokemon, setPokemon] = useState([]);
  const [selectedPokemon, setSelectedPokemon] = useState([]);

  // Robustly determine the current ranking based on the URL parameter
  const currentRanking = rankingTypes[type];

  useEffect(() => {
    if (currentRanking) {
      // Function to fetch and update data
      const updateData = () => {
        const topPokemon = getTopPokemonByStats(currentRanking.stat, 100);
        setPokemon(prev => {
          // Simple check to avoid unnecessary re-renders if data hasn't changed
          if (JSON.stringify(prev) !== JSON.stringify(topPokemon)) {
            return topPokemon;
          }
          return prev;
        });
      };

      // Initial fetch
      updateData();
      
      // Reset selection when changing ranking type
      setSelectedPokemon([]);

      // Set up a polling interval to catch updates from asynchronous data loading
      // This ensures the page updates automatically when pokemonData.js finishes loading external data
      const intervalId = setInterval(updateData, 1000);

      // Cleanup interval on unmount or when type changes
      return () => clearInterval(intervalId);
    }
  }, [type, currentRanking]);

  const togglePokemonSelection = (pokemonId) => {
    setSelectedPokemon(prev => 
      prev.includes(pokemonId)
        ? prev.filter(id => id !== pokemonId)
        : [...prev, pokemonId]
    );
  };

  const getMedalStyle = (rank) => {
    if (rank === 1) return 'medal-gold';
    if (rank === 2) return 'medal-silver';
    if (rank === 3) return 'medal-bronze';
    return 'bg-gray-100 text-gray-700';
  };

  if (!currentRanking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <SEO title="Ranking Not Found - Pokemon Stats Database" />
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Ranking Not Found</h1>
          <p className="text-gray-600 mb-6">The ranking type you're looking for doesn't exist.</p>
          <Link to="/" className="text-blue-600 hover:text-blue-800 font-medium">
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  const breadcrumbItems = [
    { name: 'Top Rankings', path: '/top/fastest' },
    { name: currentRanking.title }
  ];

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": `${currentRanking.title} - Top 100 Pokemon Rankings`,
    "description": `${currentRanking.description}. Complete top 100 list with detailed stats and information.`,
    "url": `https://example.com/top/${type}`,
    "isPartOf": {
      "@type": "WebSite",
      "name": "Pokemon Stats Database",
      "url": "https://example.com"
    }
  };

  return (
    <motion.div
      key={type} // Ensure animation triggers on type change
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-gray-50 py-8"
    >
      <SEO
        title={`${currentRanking.title} - Top 100 Pokemon Rankings`}
        description={`${currentRanking.description}. Discover the top 100 Pokemon with the highest ${currentRanking.stat} stats, complete with detailed information and comparisons.`}
        keywords={`top pokemon ${currentRanking.stat}, ${currentRanking.title.toLowerCase()}, pokemon rankings, best pokemon ${currentRanking.stat}`}
        url={`/top/${type}`}
        schema={pageSchema}
      />

      <div className="container mx-auto px-4">
        <Breadcrumb items={breadcrumbItems} />

        {/* Header */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="text-center mb-12"
        >
          <div className="flex justify-center mb-4">
            <div className={`p-4 rounded-full bg-${currentRanking.color}-100`}>
              <SafeIcon icon={currentRanking.icon} className={`w-8 h-8 text-${currentRanking.color}-600`} />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">{currentRanking.title}</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {currentRanking.description}
          </p>
        </motion.div>

        {/* Top 3 Podium */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="mb-12"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {pokemon.slice(0, 3).map((pokemon, index) => (
              <motion.div
                key={pokemon.id}
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 + index * 0.1 }}
                className={`bg-white rounded-lg shadow-lg p-6 text-center relative overflow-hidden ${
                  index === 0 ? 'md:order-2 transform md:scale-110 z-10' : 
                  index === 1 ? 'md:order-1' : 'md:order-3'
                }`}
              >
                <div className={`absolute top-0 left-0 right-0 h-2 ${
                  index === 0 ? 'bg-yellow-400' : 
                  index === 1 ? 'bg-gray-400' : 'bg-orange-400'
                }`} />
                
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full font-bold text-white mb-4 ${getMedalStyle(index + 1)}`}>
                  {index + 1}
                </div>
                
                <Link to={`/pokemon/${pokemon.name}`} className="block group">
                  <img
                    src={pokemon.image}
                    alt={pokemon.displayName}
                    className="w-24 h-24 object-contain mx-auto mb-4 group-hover:scale-110 transition-transform"
                  />
                  
                  <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                    {pokemon.displayName}
                  </h3>
                  
                  <div className="flex justify-center space-x-1 mb-3">
                    {pokemon.types.map(type => (
                      <TypeBadge key={type} type={type} size="sm" clickable={false} />
                    ))}
                  </div>
                  
                  <div className={`text-3xl font-bold text-${currentRanking.color}-600 mb-1`}>
                    {pokemon.baseStats[currentRanking.stat]}
                  </div>
                  <div className="text-sm text-gray-600 capitalize">
                    {currentRanking.stat.replace(/([A-Z])/g, ' $1').toLowerCase()}
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Rankings Table */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-lg shadow-lg overflow-hidden"
        >
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
              <h2 className="text-xl font-bold text-gray-900 mb-4 sm:mb-0">
                Complete Top 100 Rankings
              </h2>
              
              {selectedPokemon.length > 0 && (
                <Link
                  to={`/compare?pokemon=${selectedPokemon.map(id => pokemon.find(p => p.id === id)?.name).join(',')}`}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors focus:ring-2 focus:ring-blue-500"
                >
                  <SafeIcon icon={FiStar} className="w-4 h-4" />
                  <span>Compare Selected ({selectedPokemon.length})</span>
                </Link>
              )}
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm" role="table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left font-semibold text-gray-900">Compare</th>
                  <th className="px-6 py-3 text-left font-semibold text-gray-900">Rank</th>
                  <th className="px-6 py-3 text-left font-semibold text-gray-900">Pokemon</th>
                  <th className="px-6 py-3 text-left font-semibold text-gray-900">Types</th>
                  <th className="px-6 py-3 text-center font-semibold text-gray-900 capitalize">
                    {currentRanking.stat.replace(/([A-Z])/g, ' $1')}
                  </th>
                  <th className="px-6 py-3 text-center font-semibold text-gray-900">Total Stats</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {pokemon.map((pokemon, index) => {
                  const rank = index + 1;
                  const totalStats = Object.values(pokemon.baseStats).reduce((sum, stat) => sum + stat, 0);
                  const isSelected = selectedPokemon.includes(pokemon.id);
                  
                  return (
                    <motion.tr
                      key={pokemon.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 + index * 0.02 }}
                      className={`hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50' : ''}`}
                    >
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => togglePokemonSelection(pokemon.id)}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          aria-label={`Select ${pokemon.displayName} for comparison`}
                        />
                      </td>
                      
                      <td className="px-6 py-4">
                        <div className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm ${getMedalStyle(rank)}`}>
                          {rank}
                        </div>
                      </td>
                      
                      <td className="px-6 py-4">
                        <Link 
                          to={`/pokemon/${pokemon.name}`}
                          className="flex items-center space-x-3 hover:text-blue-600 transition-colors focus:ring-2 focus:ring-blue-500 rounded"
                        >
                          <img
                            src={pokemon.image}
                            alt={pokemon.displayName}
                            className="w-12 h-12 object-contain"
                          />
                          <div>
                            <div className="font-semibold text-gray-900">{pokemon.displayName}</div>
                            <div className="text-gray-500">#{pokemon.id.toString().padStart(3, '0')}</div>
                          </div>
                        </Link>
                      </td>
                      
                      <td className="px-6 py-4">
                        <div className="flex space-x-1">
                          {pokemon.types.map(type => (
                            <TypeBadge key={type} type={type} size="sm" clickable={false} />
                          ))}
                        </div>
                      </td>
                      
                      <td className="px-6 py-4 text-center">
                        <div className={`inline-flex items-center justify-center w-16 h-8 rounded-full font-bold text-${currentRanking.color}-600 bg-${currentRanking.color}-100`}>
                          {pokemon.baseStats[currentRanking.stat]}
                        </div>
                      </td>
                      
                      <td className="px-6 py-4 text-center">
                        <div className="font-semibold text-gray-900">{totalStats}</div>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Other Rankings */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-12"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Other Rankings</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.entries(rankingTypes)
              .filter(([key]) => key !== type)
              .map(([key, ranking]) => (
                <Link
                  key={key}
                  to={`/top/${key}`}
                  className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow focus:ring-2 focus:ring-blue-500 group"
                >
                  <div className="flex items-center space-x-4 mb-4">
                    <div className={`p-3 rounded-lg bg-${ranking.color}-100 group-hover:bg-${ranking.color}-200 transition-colors`}>
                      <SafeIcon icon={ranking.icon} className={`w-6 h-6 text-${ranking.color}-600`} />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                      {ranking.title}
                    </h3>
                  </div>
                  <p className="text-gray-600 text-sm">
                    {ranking.description}
                  </p>
                </Link>
              ))}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}

export default TopRankingsPage;